import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components';
import {
  NotificationRequest,
  NotificationRequestStatus,
  NotificationRequestType,
  SubmitNotificationBody,
  cancelNotificationRequest,
  deleteNotificationRequest,
  listNotificationRequests,
  submitNotification,
  updateNotificationRequest,
} from '../../../services/notificationConfigsService';

const PAGE_SIZE = 20;

/** Casbin resource guarding every /notification-configs write. */
const PERMISSION_RESOURCE = 'notification-configs';

const STATUS_CHOICES: NotificationRequestStatus[] = [
  'SUBMITTED',
  'PENDING',
  'PROCESSING',
  'CANCELLED',
  'FAILED',
];

const TYPE_CHOICES: NotificationRequestType[] = ['IMMEDIATE', 'SCHEDULED'];

const TYPE_COLOR: Record<NotificationRequestType, 'secondary' | 'warning'> = {
  IMMEDIATE: 'secondary',
  SCHEDULED: 'warning',
};

const STATUS_COLOR: Record<
  NotificationRequestStatus,
  'default' | 'primary' | 'info' | 'warning' | 'error' | 'success'
> = {
  SUBMITTED: 'primary',
  PENDING: 'warning',
  PROCESSING: 'info',
  CANCELLED: 'default',
  FAILED: 'error',
};

// ── Submit / Edit Dialog ──────────────────────────────────────────────────────

const defaultForm = {
  userIds: '',
  deviceTokenIds: '',
  targetAudience: 'SPECIFIED_UID',
  templateCode: '',
  variables: '{}',
  translatedVariables: '{}',
  metadataData: '{}',
  maxRetryCount: '0',
  requestId: '',
  requestType: 'IMMEDIATE',
  sendAt: '',
};

type FormState = typeof defaultForm;

const asJsonText = (value: unknown) =>
  value === null || value === undefined ? '{}' : JSON.stringify(value);

/** ISO instant → the `YYYY-MM-DDTHH:mm` local-time shape `datetime-local` expects. */
const toLocalInputValue = (iso: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTime() - date.getTimezoneOffset() * 60_000;
  return new Date(offsetMs).toISOString().slice(0, 16);
};

const recordToForm = (record: NotificationRequest): FormState => ({
  userIds: (record.payload.recipient?.userIds ?? []).join(', '),
  deviceTokenIds: (record.payload.recipient?.deviceTokenIds ?? []).join(', '),
  targetAudience: record.payload.recipient?.targetAudience ?? 'ALL_USER',
  templateCode: record.payload.content?.templateCode ?? '',
  variables: asJsonText(record.payload.content?.variables),
  translatedVariables: asJsonText(record.payload.content?.translatedVariables),
  metadataData: asJsonText(record.payload.metadata?.data),
  maxRetryCount: String(record.payload.delivery?.maxRetryCount ?? 0),
  requestId: record.payload.requestId ?? '',
  requestType: record.type,
  sendAt: toLocalInputValue(record.sendAt),
});

const splitIds = (value: string) =>
  value
    ? value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

const formToPayload = (form: FormState): SubmitNotificationBody['payload'] => ({
  recipient: {
    userIds: splitIds(form.userIds),
    deviceTokenIds: splitIds(form.deviceTokenIds),
    targetAudience: form.targetAudience,
  },
  content: {
    templateCode: form.templateCode,
    variables: form.variables,
    translatedVariables: form.translatedVariables,
  },
  metadata: {
    data: form.metadataData,
  },
  delivery: {
    maxRetryCount: parseInt(form.maxRetryCount, 10) || 0,
  },
  requestId: form.requestId,
});

export const SubmitDialog = ({
  record,
  onClose,
  onSaved,
}: {
  record?: NotificationRequest;
  onClose: () => void;
  onSaved?: () => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const isEdit = Boolean(record);
  const [form, setForm] = useState<FormState>(record ? recordToForm(record) : defaultForm);
  const [loading, setLoading] = useState(false);

  const set =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const scheduledAt =
        form.requestType === 'SCHEDULED' && form.sendAt
          ? { sendAt: new Date(form.sendAt).toISOString() }
          : {};

      if (isEdit) {
        await updateNotificationRequest(record!.id, {
          ...scheduledAt,
          payload: formToPayload(form),
        });
        notify(t('module.notificationConfigs.requestUpdated', { _: 'Notification updated' }), {
          type: 'success',
          autoHideDuration: 1500,
        });
      } else {
        await submitNotification({
          requestType: form.requestType as NotificationRequestType,
          ...scheduledAt,
          payload: formToPayload(form),
        });
        notify(t('module.notificationConfigs.submitSuccess', { _: 'Notification submitted' }), {
          type: 'success',
          autoHideDuration: 1500,
        });
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      notify(err?.message ?? t('module.notificationConfigs.submitFailed', { _: 'Submit failed' }), {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {isEdit
          ? t('module.notificationConfigs.editNotification', { _: 'Edit Notification' })
          : t('module.notificationConfigs.submitNotification', { _: 'Send Notification' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl size="small" fullWidth required disabled={isEdit}>
            <InputLabel required>
              {t('module.notificationConfigs.requestType', { _: 'Request Type' })}
            </InputLabel>
            <Select
              value={form.requestType}
              label={t('module.notificationConfigs.requestType', { _: 'Request Type' })}
              onChange={(e) => setForm((prev) => ({ ...prev, requestType: e.target.value }))}
            >
              {TYPE_CHOICES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.requestType === 'SCHEDULED' && (
            <TextField
              label={t('module.notificationConfigs.sendAt', { _: 'Send At' })}
              value={form.sendAt}
              onChange={set('sendAt')}
              size="small"
              fullWidth
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
            />
          )}
          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            {t('module.notificationConfigs.recipient', { _: 'Recipient' })}
          </Typography>
          <FormControl size="small" fullWidth required>
            <InputLabel required>
              {t('module.notificationConfigs.targetAudience', { _: 'Target Audience' })}
            </InputLabel>
            <Select
              value={form.targetAudience}
              label={t('module.notificationConfigs.targetAudience', { _: 'Target Audience' })}
              onChange={(e) => setForm((prev) => ({ ...prev, targetAudience: e.target.value }))}
            >
              <MenuItem value="ALL_USER">ALL_USER</MenuItem>
              <MenuItem value="SPECIFIED_UID">SPECIFIED_UID</MenuItem>
            </Select>
          </FormControl>
          {form.targetAudience === 'SPECIFIED_UID' && (
            <TextField
              label={t('module.notificationConfigs.userIds', { _: 'User IDs' })}
              value={form.userIds}
              onChange={set('userIds')}
              size="small"
              fullWidth
              multiline
              minRows={4}
              placeholder={t('module.notificationConfigs.userIdsPlaceholder', {
                _: 'uid1, uid2 (comma-separated)',
              })}
            />
          )}
          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            {t('module.notificationConfigs.content', { _: 'Content' })}
          </Typography>
          <TextField
            label={t('module.notificationConfigs.templateCode', { _: 'Template Code' })}
            value={form.templateCode}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                templateCode: e.target.value.replace(/\s+/g, ''),
              }))
            }
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t('module.notificationConfigs.variables', { _: 'Variables (JSON)' })}
            value={form.variables}
            onChange={set('variables')}
            size="small"
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label={t('module.notificationConfigs.translatedVariables', {
              _: 'Translated Variables (JSON)',
            })}
            value={form.translatedVariables}
            onChange={set('translatedVariables')}
            size="small"
            fullWidth
            multiline
            minRows={3}
          />
          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            {t('module.notificationConfigs.metadata', { _: 'Metadata' })}
          </Typography>
          <TextField
            label={t('module.notificationConfigs.metadataData', { _: 'Metadata Data (JSON)' })}
            value={form.metadataData}
            onChange={set('metadataData')}
            size="small"
            fullWidth
            multiline
            minRows={3}
          />
          {/* <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            {t('module.notificationConfigs.delivery', { _: 'Delivery' })}
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('module.notificationConfigs.maxRetryCount', { _: 'Max Retry Count' })}
              value={form.maxRetryCount}
              onChange={set('maxRetryCount')}
              size="small"
              type="number"
              sx={{ width: 160 }}
            />
            <TextField
              label={t('module.notificationConfigs.requestId', { _: 'Request ID' })}
              value={form.requestId}
              onChange={set('requestId')}
              size="small"
              fullWidth
            />
          </Stack> */}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        {/* edit → PUT /notification-configs/requests/:id, create → POST /notification-configs/submit */}
        <PermissionButton resource={PERMISSION_RESOURCE} action={isEdit ? 'update' : 'submit'}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !form.templateCode || !form.targetAudience}
          >
            {isEdit ? t('actions.save', { _: 'Save' }) : t('actions.submit', { _: 'Submit' })}
          </Button>
        </PermissionButton>
      </DialogActions>
    </>
  );
};

// ── Confirm Dialog (cancel / delete) ──────────────────────────────────────────

const ConfirmDialog = ({
  mode,
  record,
  onClose,
  onDone,
}: {
  mode: 'cancel' | 'delete';
  record: NotificationRequest;
  onClose: () => void;
  onDone: () => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (mode === 'cancel') {
        await cancelNotificationRequest(record.id);
        notify(t('module.notificationConfigs.requestCancelled', { _: 'Notification cancelled' }), {
          type: 'success',
          autoHideDuration: 1500,
        });
      } else {
        await deleteNotificationRequest(record.id);
        notify(t('module.notificationConfigs.requestDeleted', { _: 'Notification deleted' }), {
          type: 'success',
          autoHideDuration: 1500,
        });
      }
      onDone();
      onClose();
    } catch (err: any) {
      notify(err?.message ?? t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {mode === 'cancel'
          ? t('module.notificationConfigs.cancelNotification', { _: 'Cancel Notification' })
          : t('module.notificationConfigs.deleteNotification', { _: 'Delete Notification' })}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {mode === 'cancel'
            ? t('module.notificationConfigs.cancelNotificationConfirm', {
                _: 'Cancel this scheduled notification? It will not be sent.',
              })
            : t('module.notificationConfigs.deleteNotificationConfirm', {
                _: 'Delete this notification request? This action cannot be undone.',
              })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        {/* cancel → POST /notification-configs/requests/:id/cancel, delete → DELETE the request */}
        <PermissionButton
          resource={PERMISSION_RESOURCE}
          action={mode === 'cancel' ? 'update' : 'delete'}
        >
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={loading}>
            {mode === 'cancel' ? t('actions.confirm', { _: 'Confirm' }) : t('actions.delete')}
          </Button>
        </PermissionButton>
      </DialogActions>
    </>
  );
};

// ── Detail Dialog ─────────────────────────────────────────────────────────────

/** Values arrive either already parsed or as a JSON string, depending on how they were submitted. */
const prettyJson = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    if (!value.trim()) return null;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
};

const DetailField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ mt: 0.25 }}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  </Box>
);

const JsonBlock = ({ label, value }: { label: string; value: unknown }) => {
  const text = prettyJson(value);
  return (
    <DetailField label={label}>
      {text ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontSize: 12,
            overflow: 'auto',
            maxHeight: 200,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {text}
        </Box>
      ) : (
        '-'
      )}
    </DetailField>
  );
};

const DetailDialog = ({
  record,
  onClose,
}: {
  record: NotificationRequest;
  onClose: () => void;
}) => {
  const t = useTranslate();
  const { content, metadata, recipient } = record.payload;
  const userIds = recipient?.userIds ?? [];

  return (
    <>
      <DialogTitle>
        {t('module.notificationConfigs.notificationDetail', { _: 'Notification Detail' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DetailField label={t('common.id', { _: 'ID' })}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {record.id}
            </Typography>
          </DetailField>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <DetailField label={t('module.notificationConfigs.requestType', { _: 'Type' })}>
              <Chip
                label={record.type}
                size="small"
                variant="outlined"
                color={TYPE_COLOR[record.type]}
              />
            </DetailField>
            <DetailField label={t('module.notificationConfigs.status', { _: 'Status' })}>
              <Chip label={record.status} size="small" color={STATUS_COLOR[record.status]} />
            </DetailField>
            <DetailField label={t('module.notificationConfigs.createdAt', { _: 'Created At' })}>
              {new Date(record.createdAt).toLocaleString()}
            </DetailField>
            {record.sendAt && (
              <DetailField label={t('module.notificationConfigs.sendAt', { _: 'Send At' })}>
                {new Date(record.sendAt).toLocaleString()}
              </DetailField>
            )}
          </Stack>
          {record.errorMessage && (
            <DetailField
              label={t('module.notificationConfigs.errorMessage', { _: 'Error Message' })}
            >
              <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
                {record.errorMessage}
              </Typography>
            </DetailField>
          )}
          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            {t('module.notificationConfigs.recipient', { _: 'Recipient' })}
          </Typography>
          {userIds.length > 0 ? (
            <>
              <DetailField label={t('module.notificationConfigs.userIds', { _: 'User IDs' })}>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  <Table size="small">
                    <TableBody>
                      {userIds.map((id, index) => (
                        <TableRow key={id} hover>
                          <TableCell sx={{ width: 40, color: 'text.secondary' }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {id}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </DetailField>
            </>
          ) : (
            'All_USER'
          )}
          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            {t('module.notificationConfigs.content', { _: 'Content' })}
          </Typography>
          <DetailField label={t('module.notificationConfigs.templateCode', { _: 'Template Code' })}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {content?.templateCode ?? '-'}
            </Typography>
          </DetailField>
          <JsonBlock
            label={t('module.notificationConfigs.variables', { _: 'Variables' })}
            value={content?.variables}
          />
          <JsonBlock
            label={t('module.notificationConfigs.translatedVariables', {
              _: 'Translated Variables',
            })}
            value={content?.translatedVariables}
          />
          <JsonBlock
            label={t('module.notificationConfigs.metadataData', { _: 'Metadata Data' })}
            value={metadata?.data}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('actions.close', { _: 'Close' })}
        </Button>
      </DialogActions>
    </>
  );
};

// ── Panel ─────────────────────────────────────────────────────────────────────

export const RecordsPanel = () => {
  const t = useTranslate();
  const [items, setItems] = useState<NotificationRequest[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<NotificationRequestStatus | ''>('');
  const [type, setType] = useState<NotificationRequestType | ''>('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState<NotificationRequest | null>(null);
  const [editRecord, setEditRecord] = useState<NotificationRequest | null>(null);
  const [confirm, setConfirm] = useState<{
    mode: 'cancel' | 'delete';
    record: NotificationRequest;
  } | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listNotificationRequests({
        ...(status ? { status } : {}),
        ...(type ? { type } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [page, status, type]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const resetToFirstPage = () => setPage(0);

  return (
    <Card sx={{ mt: 2 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'start',
          flexWrap: 'wrap',
          '& .MuiInputBase-root': { minHeight: 40 },
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('module.notificationConfigs.status', { _: 'Status' })}</InputLabel>
          <Select
            value={status}
            label={t('module.notificationConfigs.status', { _: 'Status' })}
            onChange={(e) => {
              setStatus(e.target.value as NotificationRequestStatus | '');
              resetToFirstPage();
            }}
          >
            <MenuItem value="">{t('actions.all', { _: 'All' })}</MenuItem>
            {STATUS_CHOICES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>
            {t('module.notificationConfigs.requestType', { _: 'Request Type' })}
          </InputLabel>
          <Select
            value={type}
            label={t('module.notificationConfigs.requestType', { _: 'Request Type' })}
            onChange={(e) => {
              setType(e.target.value as NotificationRequestType | '');
              resetToFirstPage();
            }}
          >
            <MenuItem value="">{t('actions.all', { _: 'All' })}</MenuItem>
            {TYPE_CHOICES.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          disabled={!status && !type}
          onClick={() => {
            setStatus('');
            setType('');
            resetToFirstPage();
          }}
          sx={{ minHeight: 40 }}
        >
          {t('common.reset', { _: 'Reset' })}
        </Button>
        <Box sx={{ flex: 1 }} />
        <PermissionButton resource={PERMISSION_RESOURCE} action="submit">
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => setSubmitOpen(true)}
            sx={{ minHeight: 40 }}
          >
            {t('module.notificationConfigs.pushNewNotification', { _: 'Push New Notification' })}
          </Button>
        </PermissionButton>
      </Box>

      <Card>
        <Box sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.id', { _: 'ID' })}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('module.notificationConfigs.templateCode', { _: 'Template Code' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('module.notificationConfigs.requestType', { _: 'Type' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('module.notificationConfigs.status', { _: 'Status' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('module.notificationConfigs.recipient', { _: 'Recipient' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('module.notificationConfigs.sendAt', { _: 'Send At' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('module.notificationConfigs.createdAt', { _: 'Created At' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" variant="body2">
                      {t('common.loading', { _: 'Loading...' })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" variant="body2">
                      {t('ra.navigation.no_results')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => {
                  const canEdit = row.type === 'SCHEDULED' && row.status === 'PENDING';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{row.id ?? '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {row.payload.content?.templateCode ?? '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.type}
                          size="small"
                          variant="outlined"
                          color={TYPE_COLOR[row.type]}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.errorMessage ?? ''} arrow>
                          <Chip label={row.status} size="small" color={STATUS_COLOR[row.status]} />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(row?.payload?.recipient?.userIds?.length ?? 0) > 0
                          ? 'SPECIFIED_UID'
                          : 'ALL_USER'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.sendAt ? (
                          <Tooltip title={row.sendAt} arrow>
                            <span>{new Date(row.sendAt).toLocaleString()}</span>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={row.createdAt} arrow>
                          <span>{new Date(row.createdAt).toLocaleString()}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() => setDetailRecord(row)}
                          >
                            {t('actions.view', { _: 'View' })}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!canEdit}
                            onClick={() => setEditRecord(row)}
                          >
                            {t('actions.edit')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={!canEdit}
                            onClick={() => setConfirm({ mode: 'cancel', record: row })}
                          >
                            {t('actions.cancel')}
                          </Button>
                          {/* <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={!canEdit}
                            onClick={() => setConfirm({ mode: 'delete', record: row })}
                          >
                            {t('actions.delete')}
                          </Button> */}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={-1}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          nextIconButtonProps={{ disabled: loading || items.length < PAGE_SIZE }}
          labelDisplayedRows={({ from, to }) => `${from}-${to}`}
        />
      </Card>

      <Dialog open={submitOpen} onClose={() => setSubmitOpen(false)} maxWidth="sm" fullWidth>
        <SubmitDialog onClose={() => setSubmitOpen(false)} onSaved={fetch} />
      </Dialog>

      <Dialog
        open={Boolean(detailRecord)}
        onClose={() => setDetailRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailRecord && (
          <DetailDialog record={detailRecord} onClose={() => setDetailRecord(null)} />
        )}
      </Dialog>

      <Dialog
        open={Boolean(editRecord)}
        onClose={() => setEditRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        {editRecord && (
          <SubmitDialog record={editRecord} onClose={() => setEditRecord(null)} onSaved={fetch} />
        )}
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (
          <ConfirmDialog
            mode={confirm.mode}
            record={confirm.record}
            onClose={() => setConfirm(null)}
            onDone={fetch}
          />
        )}
      </Dialog>
    </Card>
  );
};
